import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Save, XCircle } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Pager, Toolbar, Select, RefreshButton, Status, Tag, ConfirmButton, Drawer } from '../ui'
import { get, post, patch, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, compact, fa, faDate, num } from '../../lib/format'

export default function StakingAdmin() {
  const [tab, setTab] = useState('plans')
  return (
    <>
      <PageHead title="استیکینگ" subtitle="مدیریت طرح‌ها و سرمایه‌گذاری‌های کاربران" />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {[['plans', 'طرح‌ها'], ['stakes', 'سرمایه‌گذاری‌ها']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>{l}</button>
        ))}
      </div>
      {tab === 'plans' ? <Plans /> : <Stakes />}
    </>
  )
}

const EMPTY = { currency: 'USDT', apr: 20, days: 30, min_amount: 10, capacity: 10000, active: true }

function Plans() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [edit, setEdit] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get('/admin/content/staking/plans').then(setRows).catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const save = async () => {
    const body = {
      currency: edit.currency, apr: Number(edit.apr), days: Number(edit.days),
      min_amount: Number(edit.min_amount ?? edit.minAmount),
      capacity: Number(edit.capacity), active: edit.active,
    }
    try {
      if (edit.id) await patch(`/admin/content/staking/plans/${edit.id}`, body)
      else await post('/admin/content/staking/plans', body)
      toast.ok('ذخیره شد'); setEdit(null); load()
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <>
      <Panel dense action={
        <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary !py-2 text-xs">
          <Plus className="h-3.5 w-3.5" /> طرح جدید
        </button>
      } title="طرح‌های استیکینگ">
        <DataTable rows={rows} loading={loading} minWidth={700} empty="طرحی تعریف نشده است"
          onRowClick={(r: any) => setEdit({ ...r, min_amount: r.minAmount })}
          columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            { key: 'c', header: 'ارز', cell: (r: any) => <span className="num text-slate-100">{r.currency}</span> },
            { key: 'a', header: 'سود سالانه', cell: (r: any) => <span className="num text-up">{num(r.apr, 1)}٪</span> },
            { key: 'd', header: 'مدت', cell: (r: any) => <span className="num text-slate-300">{fa(r.days)} روز</span> },
            { key: 'm', header: 'حداقل', cell: (r: any) => <span className="num text-slate-300">{num(r.minAmount, 6)}</span> },
            {
              key: 'cap', header: 'ظرفیت', cell: (r: any) => (
                <div className="min-w-[7rem]">
                  <div className="num mb-1 text-[11px] text-slate-400">{num(r.used, 4)} / {num(r.capacity, 4)}</div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/[.06]">
                    <div className="h-full bg-gradient-to-l from-brand-600 to-up"
                      style={{ width: `${Math.min((r.used / (r.capacity || 1)) * 100, 100)}%` }} />
                  </div>
                </div>
              ),
            },
            { key: 's', header: 'وضعیت', cell: (r: any) => r.active ? <Tag kind="up">فعال</Tag> : <Tag kind="muted">غیرفعال</Tag> },
          ]} />
      </Panel>

      <Drawer open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? 'ویرایش طرح' : 'طرح جدید'}>
        {edit && (
          <>
            {([['currency', 'ارز'], ['apr', 'سود سالانه (درصد)'], ['days', 'مدت (روز)'],
               ['min_amount', 'حداقل مبلغ'], ['capacity', 'ظرفیت کل']] as const).map(([k, l]) => (
              <div key={k} className="mb-3">
                <label className="label">{l}</label>
                <input className="input num" dir={k === 'currency' ? 'ltr' : 'ltr'} value={(edit as any)[k] ?? ''}
                  onChange={(e) => setEdit({ ...edit, [k]: k === 'currency' ? e.target.value.toUpperCase() : e.target.value })} />
              </div>
            ))}
            <label className="mb-4 flex cursor-pointer items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" className="h-4 w-4 accent-brand-500" checked={edit.active}
                onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
              طرح فعال باشد
            </label>
            <button onClick={save} className="btn-primary w-full !py-3"><Save className="h-4 w-4" /> ذخیره</button>
          </>
        )}
      </Drawer>
    </>
  )
}

function Stakes() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/content/staking/stakes${qs({ status, page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [status, page])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
            options={[['', 'همه'], ['active', 'فعال'], ['finished', 'پایان‌یافته'], ['canceled', 'لغو شده']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={820} empty="سرمایه‌گذاری ثبت نشده است" columns={[
        { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r: any) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'c', header: 'ارز', cell: (r: any) => <span className="num text-slate-200">{r.currency}</span> },
        { key: 'a', header: 'مبلغ', cell: (r: any) => <span className="num text-white">{num(r.amount, 6)}</span> },
        { key: 'p', header: 'سود سالانه', cell: (r: any) => <span className="num text-up">{num(r.apr, 1)}٪</span> },
        { key: 'd', header: 'مدت', cell: (r: any) => <span className="num text-slate-300">{fa(r.days)} روز</span> },
        { key: 'r', header: 'سود پرداختی', cell: (r: any) => <span className="num text-slate-300">{num(r.rewardPaid, 8)}</span> },
        { key: 's', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
        { key: 'dt', header: 'شروع', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt, false)}</span> },
        {
          key: 'x', header: '', cell: (r: any) => r.status === 'active' && (
            <ConfirmButton variant="down" label="لغو" question="اصل سرمایه بازگردانده شود؟"
              icon={<XCircle className="h-3.5 w-3.5" />}
              onConfirm={async () => {
                try { await post(`/admin/content/staking/stakes/${r.id}/cancel`); toast.ok('لغو شد'); load() }
                catch (e: any) { toast.err(e.message) }
              }} />
          ),
        },
      ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}
